import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import Monogram from "@/components/Monogram";
import Ornament from "@/components/Ornament";
import Footer from "@/components/Footer";
import SearchResults from "@/components/SearchResults";
import { StandardCategoryCard, CocktailCategoryCard } from "@/components/CategoryCards";
import { useMenu } from "@/lib/api";
import LanguageDropdown from "@/components/LanguageDropdown";
import { useLang, t } from "@/lib/i18n";

export default function MenuPage() {
  const [query, setQuery] = useState("");
  const lang = useLang();
  const { data, isLoading: loading } = useMenu();
  const categories = useMemo(() => data?.categories || [], [data]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const out = [];
    for (const cat of categories) {
      for (const prod of cat.products || []) {
        const hay = `${prod.name} ${Object.values(prod.translations || {}).join(" ")} ${prod.description || ""} ${(prod.ingredients || []).join(" ")}`.toLowerCase();
        if (hay.includes(q)) out.push({ ...prod, categoryName: cat.name, categorySlug: cat.slug });
      }
    }
    return out;
  }, [query, categories]);

  const standard = categories.filter((c) => c.kind !== "cocktail");
  const cocktails = categories.filter((c) => c.kind === "cocktail");

  return (
    <div data-testid="menu-page" className="min-h-screen flex flex-col texture-grain">
      <header className="sticky top-0 z-20 backdrop-blur-md bg-[var(--header-bg)] border-b border-[#C5A059]/25">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" data-testid="header-home-link" className="shrink-0">
            <Monogram size="sm" />
          </Link>
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--muted)]" />
            <input
              data-testid="search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(lang, "searchPh")}
              className="w-full rounded-full border border-[var(--line)] bg-[var(--card)] pl-10 pr-9 py-2.5 text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/25"
            />
            {query && (
              <button
                data-testid="search-clear-button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--bordeaux)]"
                aria-label="Cancella ricerca"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <LanguageDropdown />
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 pb-12">
        {loading && (
          <p data-testid="menu-loading" className="text-center text-[var(--muted)] py-16 font-display italic text-xl">
            {t(lang, "loading")}
          </p>
        )}

        {!loading && results && <SearchResults results={results} query={query} lang={lang} />}

        {!loading && !results && (
          <>
            <section className="pt-7">
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--bordeaux)] font-semibold text-center">{t(lang, "ourMenu")}</h2>
              <Ornament />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {standard.map((cat, i) => (
                  <StandardCategoryCard key={cat.slug} cat={cat} index={i} lang={lang} />
                ))}
              </div>
            </section>

            <section data-testid="cocktail-section" className="mt-10 -mx-4 px-4 py-8 bg-[var(--cocktail-bg)] border-y border-[#C5A059]/30">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-center text-[var(--bordeaux)]">
                  {t(lang, "cocktailCarte")}
                </h2>
                <Ornament />
                <p className="text-center text-[var(--muted)] italic font-display text-base mb-2">
                  {t(lang, "cocktailSub")}
                </p>
                <div data-testid="cocktail-pricing-note" className="flex flex-col items-center gap-2 mb-6 px-4 text-center">
                  <span className="font-cinzel text-base sm:text-lg tracking-[0.1em] sm:tracking-[0.2em] uppercase text-[var(--bordeaux)] leading-relaxed">
                    {t(lang, "cocktailPricing1")}
                  </span>
                  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                    <rect x="2.5" y="2.5" width="5" height="5" transform="rotate(45 5 5)" fill="#C5A059" opacity="0.85" />
                  </svg>
                  <span className="font-cinzel text-base sm:text-lg tracking-[0.1em] sm:tracking-[0.2em] uppercase text-[var(--bordeaux)] leading-relaxed">
                    {t(lang, "cocktailPricing2")}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cocktails.map((cat) => (
                    <CocktailCategoryCard key={cat.slug} cat={cat} lang={lang} />
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
